import { FilterConfig, MainFilter, MainFilterForm } from 'components/tables/list-table/ListTableTypes';
import { FeatureFlag } from 'types/features';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import {
    coreTeamFilter,
    employeeFilter,
    jobRoleFilter,
    mainFilterDefaults,
    monthFilter,
    monthQuickFilter,
    quickFiltersFrom,
    singleChoiceFilter,
    yesNoFilter,
} from '../reportFilters';

export const MEASURE_SELECTOR = 'measure';
export const SPLIT_SELECTOR = 'split';
export const INCLUDE_ZERO_SELECTOR = 'includeZero';
const IS_ACTIVE_SELECTOR = 'employeeIsActive';

type Choice = { value: string; labelId: string; getShouldHide?: (mainFilters: MainFilterForm) => boolean };

/** The measure picked in the main filters; the backend reads none as Finalised hours. */
export function measureOf(mainFilters: MainFilterForm): string | undefined {
    const group = Object.values(mainFilters).find((filter) => filter.selector === MEASURE_SELECTOR);
    return group?.type === 'checkboxgroup' ? group.filters.find((option) => option.isSelected)?.argument : undefined;
}

/** The measures in the selector's order; Remuneration hours only where the tenant is offered them. */
export function measureChoices(isRemunerationOffered: boolean): Choice[] {
    return [
        { value: 'FINALISED', labelId: 'reporting.hours-measure.finalised' },
        { value: 'USED', labelId: 'reporting.hours-measure.used' },
        ...(isRemunerationOffered ? [{ value: 'REMUNERATION', labelId: 'reporting.hours-measure.remuneration' }] : []),
        { value: 'DRAFT', labelId: 'reporting.hours-measure.draft' },
    ];
}

/** The splits in the selector's order. A draft has no invoicing stage, so Draft hours rule Invoicing out. */
export const SPLIT_CHOICES: Choice[] = [
    { value: 'NONE', labelId: 'reporting.hours-split.none' },
    { value: 'BILLABILITY', labelId: 'reporting.hours-split.billability' },
    {
        value: 'INVOICING',
        labelId: 'reporting.hours-split.invoicing',
        getShouldHide: (mainFilters) => measureOf(mainFilters) === 'DRAFT',
    },
    { value: 'CATEGORY', labelId: 'reporting.hours-split.category' },
    { value: 'BREAKDOWN', labelId: 'reporting.hours-split.breakdown' },
];

/**
 * Whether the tenant is offered the Remuneration hours measure: where it controls the remuneration calculation, or
 * keeps the "Don't nullify" option, its timecards carry remuneration hours worth reading.
 */
export function useIsRemunerationOffered(): boolean {
    const isCalcTypeControlled = useIsFeatureEnabled(FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL);
    const isDontNullifyHidden = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    return isCalcTypeControlled || !isDontNullifyHidden;
}

/** A single choice whose first option is the default, as the backend reads an unset one. */
function choiceFilter(selector: string, labelId: string, choices: Choice[]): MainFilter {
    const filter = singleChoiceFilter(
        selector,
        labelId,
        choices.map((choice) => ({ label: t(choice.labelId), value: choice.value })),
        { answerWithLabel: true }
    ) as Extract<MainFilter, { type: 'checkboxgroup' }>;
    return {
        ...filter,
        filters: filter.filters.map((option, index) => ({
            ...option,
            defaultSelected: index === 0,
            getShouldHide: choices[index].getShouldHide,
        })),
    };
}

export function buildFilterConfig(isRemunerationOffered: boolean): FilterConfig {
    const filters: MainFilter[] = [
        monthFilter(),
        coreTeamFilter(),
        jobRoleFilter(),
        employeeFilter('employeeId', 'reporting.filter.employee'),
        yesNoFilter(IS_ACTIVE_SELECTOR, 'reporting.filter.is-active'),
        {
            type: 'checkboxgroup',
            selector: INCLUDE_ZERO_SELECTOR,
            comperator: '==',
            filters: [{ label: t('reporting.filter.include-zero-hours'), argument: 'true' }],
        },
        choiceFilter(SPLIT_SELECTOR, 'reporting.filter.split-by', SPLIT_CHOICES),
        choiceFilter(MEASURE_SELECTOR, 'reporting.filter.measure', measureChoices(isRemunerationOffered)),
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: monthQuickFilter(),
        quickFilters: quickFiltersFrom(filters, {
            [SPLIT_SELECTOR]: 'reporting.filter.split-by',
            [MEASURE_SELECTOR]: 'reporting.filter.measure',
            [IS_ACTIVE_SELECTOR]: 'reporting.filter.is-active',
        }),
        searchBarSelectors: [],
        filters,
    };
}

/** The report opens on active employees, those with no hours included. */
export const INITIAL_SEARCH_PARAMS = mainFilterDefaults([
    `${IS_ACTIVE_SELECTOR}==true`,
    `${INCLUDE_ZERO_SELECTOR}==true`,
]);
